<template>
    <div class="global-notice">
        <template v-if="modalData.popoverData">
            <Modal v-model="popoverVisable" class="ed-global-notice-modal" :width="360" :closable="false" :mask-closable="false">
                <div class="notice-icon">
                    <img :src="modalData.popoverData.icon_url || iconDefault" alt="" />
                </div>
                <div class="notice-title">
                    {{ popoverTitle }}
                </div>
                <div class="notice-body" v-html="popoverBody"></div>
                <div class="notice-today" v-show="modalData.popoverData.interval !== 0">
                    <Checkbox v-model="popoverTodayDisable">{{ $t("today-silent") }}</Checkbox>
                </div>
                <div slot="footer">
                    <Button type="primary" @click="popoverOkHandler">{{ $t("confirm") }}</Button>
                </div>
            </Modal>
        </template>
        <template v-if="modalData.forceOffData">
            <Modal v-model="forceOffVisable" class="ed-global-notice-modal" :width="360" :closable="false" :mask-closable="false">
                <div class="notice-icon">
                    <img :src="modalData.forceOffData.icon_url || iconDefault" alt="" />
                </div>
                <div class="notice-title">
                    {{ forceOffTitle }}
                </div>
                <div class="notice-body" v-html="forceOffBody"></div>
                <div slot="footer">
                    <Button type="primary" @click="forceOffOkHandler">{{ $t("confirm") }}</Button>
                </div>
            </Modal>
        </template>
        <template v-if="modalData.notifyData">
            <div v-show="notifyVisivale" class="ed-global-notify-modal">
                <div class="notify-modal-content">
                    <div class="notify-icon">
                        <img :src="modalData.notifyData.icon_url || notifyIconDefault" alt="" />
                    </div>
                    <div ref="notifyBodyRef" class="notify-body">
                        <div ref="notifyBodyTextRef" v-html="notifyBody"></div>
                        <div ref="notifyBodyAnimationRef" class="notify-body__animation">
                            <span v-html="notifyBody"></span>
                            <span v-html="notifyBody"></span>
                        </div>
                    </div>
                    <div class="notify-close" @click="notifyCloseHandler">
                        <img src="@/public/img/notify-close.png" alt="" />
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script lang="ts">
    import { Component, Prop, Vue, Watch } from "vue-property-decorator";
    import axios from "axios";
    import { isNumber, isEqual, debounce } from "lodash";
    import appConfig from "@/public/globalConfig";
    import dayjs from "dayjs";
    import iconDefault from "@/public/img/notice-default.png";
    import notifyIconDefault from "@/public/img/notify-default.png";
    import DOMPurify from "dompurify";

    enum EnumFrom {
        home = "home",
        canvas = "canvas",
    }

    enum EnumNoticeType {
        popover = "popover", // 弹框
        forceOff = "forceOff", // 强制下线弹框
        notify = "notify", // 持续提示（跑马灯）
        redirect = "redirect", // 重定向到302页面
    }

    interface NoticeItem {
        message: any;
        icon_url: string;
        type: EnumNoticeType;
        start_time: number;
        end_time: number;
        interval?: number; // 通知间隔时间，单位秒。0表示只显示一次。
        uids?: number[]; // 受影响的用户id。为空时表示所有用户。线上测试用
    }

    // 本地调试和测试环境
    const isDev = process.env.NODE_ENV === "development" || location.origin.includes("dev.edrawmax");

    @Component({
        components: {},
    })
    export default class GlobalNotice extends Vue {
        @Prop({
            default: EnumFrom.home,
        })
        from: string;

        iconDefault = iconDefault;
        notifyIconDefault = notifyIconDefault;
        popoverVisable = false;
        popoverTodayDisable = false;
        popoverOkHandler() {
            this.popoverVisable = false;
            if (this.popoverTodayDisable) {
                localStorage.setItem("globalNoticePopover", dayjs().format("YYYY-MM-DD"));
            }

            this.setPopoverTime();
        }

        forceOffVisable = false;
        forceOffOkHandler() {
            this.forceOffVisable = false;
            // 返回工作台
            location.href = `${location.origin}${appConfig.sourcePath}`;
        }

        list: NoticeItem[] = [];
        modalData = {
            popoverData: undefined,
            forceOffData: undefined,
            notifyData: undefined,
        };

        @Watch("modalData.popoverData")
        popoverDataChange() {
            if (this.from !== EnumFrom.home) {
                return;
            }

            if (this.popoverTimer) {
                clearTimeout(this.popoverTimer);
            }
            this.showPopoverModal();
        }

        @Watch("modalData.forceOffData")
        forceOffDataChange() {
            if (this.from !== EnumFrom.canvas) {
                return;
            }

            if (this.modalData.forceOffData && !this.forceOffVisable) {
                this.forceOffVisable = true;
                // 保存文件
                this.$store.dispatch("autoSaveFile");
            }
        }

        @Watch("modalData.notifyData")
        notifyDataChange() {
            if (this.from !== EnumFrom.canvas) {
                return;
            }

            if (this.notifyTimer) {
                clearTimeout(this.notifyTimer);
            }

            if (!this.notifyVisivale) {
                this.notifyVisivale = true;
            }

            this.setNotifyAnimation();
        }

        get popoverTitle() {
            const text = this.modalData.popoverData?.message?.[this.lang]?.title;
            const enText = this.modalData.popoverData?.message?.["en"]?.title;
            return text || enText || "";
        }

        get popoverBody() {
            const text = DOMPurify.sanitize(this.modalData.popoverData?.message?.[this.lang]?.body);
            const enText = DOMPurify.sanitize(this.modalData.popoverData?.message?.["en"]?.body);
            return text || enText || "";
        }

        get forceOffTitle() {
            const text = this.modalData.forceOffData?.message?.[this.lang]?.title;
            const enText = this.modalData.forceOffData?.message?.["en"]?.title;
            return text || enText || "";
        }

        get forceOffBody() {
            const text = DOMPurify.sanitize(this.modalData.forceOffData?.message?.[this.lang]?.body);
            const enText = DOMPurify.sanitize(this.modalData.forceOffData?.message?.["en"]?.body);
            return text || enText || "";
        }

        get notifyBody() {
            const text = DOMPurify.sanitize(this.modalData.notifyData?.message?.[this.lang]?.body);
            const enText = DOMPurify.sanitize(this.modalData.notifyData?.message?.["en"]?.body);
            return text || enText || "";
        }

        get userInfoApp() {
            return this.$store.state?.user?.userInfo;
        }
        @Watch("userInfoApp")
        OnUserInfoAppChange() {
            // 在app页面，最开始获取不到userInfo，等获取到之后再调用一次。为避免重复调用，使用标记执行。
            if (this.needReloadListFlag) {
                this.getList();
                this.needReloadListFlag = false;
            }
        }
        needReloadListFlag = false;

        setDataTimer = null;
        setModalData() {
            let tmpPopoverData = undefined;
            let tmpForceOffData = undefined;
            let tmpNotifyData = undefined;
            let tmpRedirectData = undefined;

            const userInfo = this.$store?.state?.userInfo || this.$store?.state?.user?.userInfo;

            this.list.forEach((item) => {
                const nowTime = new Date().getTime();
                if (item.start_time * 1000 > nowTime || item.end_time * 1000 < nowTime) {
                    return;
                }

                if (item?.uids?.length > 0) {
                    if (userInfo?.user_id === 0) {
                        this.needReloadListFlag = true;
                    }
                    if (!item.uids.includes(userInfo?.user_id)) {
                        return;
                    }
                }

                const dataItem: NoticeItem = {
                    ...item,
                };

                switch (item.type) {
                    case EnumNoticeType.popover:
                        tmpPopoverData = dataItem;
                        break;
                    case EnumNoticeType.forceOff:
                        tmpForceOffData = dataItem;
                        break;
                    case EnumNoticeType.notify:
                        tmpNotifyData = dataItem;
                        break;
                    case EnumNoticeType.redirect:
                        tmpRedirectData = dataItem;
                        break;
                    default:
                        break;
                }
            });

            if (tmpRedirectData) {
                let url = `${location.origin}/notify/notice.html`;

                if (isDev) {
                    url = "https://dev-en.edraw.ai/notify/notice.html";
                }

                location.href = `${url}?lang=${this.lang}`;
                return;
            }

            if (!isEqual(this.modalData.popoverData, tmpPopoverData)) {
                this.modalData.popoverData = tmpPopoverData;
            }

            if (!isEqual(this.modalData.forceOffData, tmpForceOffData)) {
                this.modalData.forceOffData = tmpForceOffData;
            }

            if (!isEqual(this.modalData.notifyData, tmpNotifyData)) {
                this.modalData.notifyData = tmpNotifyData;
            }

            if (!tmpPopoverData) {
                // 清除localStorage中记录的
                localStorage.removeItem("globalNoticePopover");
            }

            if (this.setDataTimer) {
                clearTimeout(this.setDataTimer);
            }

            const interval = 60 * 1000; // 1分钟设置（主要是检查时间）一次配置
            this.setDataTimer = setTimeout(this.setModalData, interval);
        }

        popoverTimer = null;
        setPopoverTime() {
            if (this.popoverVisable) {
                return;
            }

            if (this.isEndTimeExpired(this.modalData.popoverData.end_time)) {
                return;
            }

            this.popoverTimer = setTimeout(() => {
                this.showPopoverModal();
                this.setPopoverTime();
            }, this.formatInterval(this.modalData.popoverData.interval));
        }
        showPopoverModal() {
            if (this.popoverVisable) {
                return;
            }

            const dayStr = localStorage.getItem("globalNoticePopover");
            const interval = this.modalData.popoverData?.interval;

            // 今日不再提示
            if (interval > 0 && dayStr === dayjs().format("YYYY-MM-DD")) {
                return;
            }

            // 只显示一次
            if (interval === 0 && dayStr) {
                return;
            }

            this.popoverVisable = true;
            this.popoverTodayDisable = false;
            if (interval === 0) {
                localStorage.setItem("globalNoticePopover", dayjs().format("YYYY-MM-DD"));
            }
        }

        notifyTimer = null;
        notifyVisivale = false;
        setNotifyTimer() {
            if (this.modalData.notifyData) {
                if (this.isEndTimeExpired(this.modalData.notifyData.end_time)) {
                    this.notifyVisivale = false;
                    return;
                }

                this.notifyTimer = setTimeout(() => {
                    if (this.notifyVisivale) {
                        return;
                    }
                    this.notifyVisivale = true;
                }, this.formatInterval(this.modalData.notifyData.interval));
            }
        }
        notifyCloseHandler() {
            this.notifyVisivale = false;
            // this.setNotifyTimer();
        }

        getList() {
            let interval = 30 * 60 * 1000; // 30分钟获取一次
            let url = `${location.origin}/notify/config.json`;

            if (isDev) {
                // url = "http://10.90.135.122:8888/notify/edrawmax.json";
                url = "https://dev-en.edraw.ai/notify/config.json";
            }

            axios
                .get(url, {
                    responseType: "json",
                })
                .then((res) => {
                    this.list = res.data;
                })
                .catch(() => {
                    this.list = [];
                })
                .finally(() => {
                    this.setModalData();
                    setTimeout(this.getList, interval);
                });
        }

        formatInterval(utcTime: number) {
            const defaultInterval = 60 * 1000;
            if (isNumber(utcTime)) {
                return utcTime * 1000;
            }
            return defaultInterval;
        }

        isEndTimeExpired(endTime: number) {
            return endTime && endTime * 1000 < new Date().getTime();
        }

        setNotifyAnimation = debounce(() => {
            const bodyDom = this.$refs.notifyBodyRef as HTMLElement;
            const textDom = this.$refs.notifyBodyTextRef as HTMLElement;
            const animationDom = this.$refs.notifyBodyAnimationRef as HTMLElement;

            if (!bodyDom || !textDom || !animationDom) {
                return;
            }

            textDom.style.opacity = "1";
            animationDom.style.display = "none";
            this.$nextTick(() => {
                // 这里多减去4像素是因为元素间隙会占用大概4像素
                const overflowWidth = bodyDom.scrollWidth - bodyDom.clientWidth - 4;
                if (overflowWidth > 0) {
                    textDom.style.opacity = "0";
                    animationDom.style.display = "flex";
                    animationDom.style.animationDuration = `${20 + Math.floor(overflowWidth / 50)}s`;
                }
            });
        }, 100);

        mounted() {
            this.getList();
            window.addEventListener("resize", this.setNotifyAnimation);
        }

        beforeDestroy() {
            window.removeEventListener("resize", this.setNotifyAnimation);
        }
    }
</script>

<style lang="less" scoped>
    .global-notice {
        position: fixed;
        z-index: 100;
    }
</style>

<style lang="less">
    .ed-global-notice-modal > div {
        z-index: 10000 !important;
    }
    .ed-global-notice-modal {
        color: #222;
        .ivu-modal-wrap {
            display: flex;
            align-items: center;
            justify-content: center;
        }
        .ivu-modal {
            top: 0;
        }
        .ivu-modal-content {
            border: 0;
            background: linear-gradient(180deg, rgba(255, 255, 255, 0.58) 0%, #ffffff 21.33%),
                linear-gradient(90deg, rgba(134, 233, 255, 0.9) 0%, rgba(130, 157, 255, 0.8) 100%), #ffffff;
        }
        .ivu-modal-wrap .ivu-modal-body {
            padding: 0;
        }

        .notice-icon {
            display: flex;
            justify-content: center;
            min-height: 24px;
        }
        .notice-title {
            padding: 0 24px;
            font-size: 18px;
            font-weight: 700;
            line-height: 24px;
            text-align: center;
        }
        .notice-body {
            padding: 16px 24px 0 24px;
            font-size: 14px;
            font-weight: 400;
            line-height: 22px;
        }
        .notice-today {
            padding: 24px 24px 0;

            .ivu-checkbox {
                margin-right: 8px;
            }
        }
        .ivu-modal-footer {
            border: none;
            padding: 24px;
            .ivu-btn {
                display: inline-flex;
                align-items: center;
                height: 28px;
                padding: 0 24px;
            }
        }
    }
    .ed-global-notify-modal {
        display: flex;
        justify-content: center;
        position: fixed;
        top: 100px;
        width: 100%;
        pointer-events: none;
        .notify-modal-content {
            position: relative;
            display: flex;
            align-items: center;
            max-width: 1200px;
            padding: 12px 36px 12px 16px;
            z-index: 10000;
            border-radius: 8px;
            background: rgba(0, 6, 14, 0.9);
            pointer-events: all;

            @media screen and (max-width: 1919px) {
                max-width: 960px;
            }

            .notify-icon {
                width: 24px;
                height: 24px;
                margin-right: 8px;
                img {
                    width: 24px;
                    height: 24px;
                }
            }
            .notify-body {
                position: relative;
                max-width: calc(100% - 36px);
                padding-right: 4px;
                overflow: hidden;
                color: #ffffff;
                font-size: 14px;
                white-space: nowrap;
            }
            .notify-body__animation {
                display: none;
                position: absolute;
                left: 0;
                top: 0;
                animation: 20s move infinite linear;
                span {
                    margin-right: 36px;
                }

                &:hover {
                    animation-play-state: paused;
                }
            }
            .notify-close {
                position: absolute;
                top: 16px;
                right: 12px;
                width: 16px;
                height: 16px;
            }
        }
    }

    @keyframes move {
        0% {
            transform: translateX(0);
        }
        100% {
            transform: translateX(-50%);
        }
    }
</style>

<i18n>
{
    "zh": {
        "today-silent": "今日不再提示",
        "confirm": "确认"
    },
    "en": {
        "today-silent": "Don't show again today",
        "confirm": "OK"
    },
    "jp": {
        "today-silent": "今後表示しない",
        "confirm": "OK"
    },
    "de": {
        "today-silent": "Heute nicht erneut anzeigen",
        "confirm": "Sichern"
    },
    "fr": {
        "today-silent": "Ne plus afficher aujourd'hui",
        "confirm": "Confirmer"
    },
    "es": {
        "today-silent": "No volver a mostrar hoy",
        "confirm": "OK"
    },
    "pt": {
        "today-silent": "Não mostrar novamente hoje",
        "confirm": "OK"
    },
    "it": {
        "today-silent": "Non mostrare più oggi",
        "confirm": "OK"
    },
    "tw": {
        "today-silent": "Don't show again today",
        "confirm": "確認"
    },
    "kr": {
        "today-silent": "Don't show again today",
        "confirm": "확인"
    }
}
</i18n>
